import { useState } from "react";
import { Play, X, Clock, Filter } from "lucide-react";
import { useWorldStore } from "../../stores/world.js";
import { useRuns, useStartRun, useCancelRun } from "../../api/hooks.js";
import { StatusBadge } from "../common/StatusBadge.js";
import { formatDistanceToNow } from "date-fns";

type StatusFilter = "all" | "running" | "completed" | "failed";

export function RunPanel() {
  const selectedTopologyId = useWorldStore((s) => s.selectedTopologyId);
  const { selectedRunId, selectRun } = useWorldStore();
  const { data: runs } = useRuns(selectedTopologyId ?? undefined);
  const startRun = useStartRun();
  const cancelRun = useCancelRun();

  const [input, setInput] = useState("");
  const [supervised, setSupervised] = useState(false);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const handleStartRun = () => {
    if (!selectedTopologyId || !input.trim()) return;
    startRun.mutate({
      topologyId: selectedTopologyId,
      input: input.trim(),
      supervised,
    });
    setInput("");
  };

  const filteredRuns = (runs ?? [])
    .slice()
    .reverse()
    .filter((r) => {
      if (statusFilter === "all") return true;
      if (statusFilter === "running") return r.status === "running" || r.status === "paused";
      if (statusFilter === "completed") return r.status === "completed";
      if (statusFilter === "failed") return r.status === "failed" || r.status === "cancelled";
      return true;
    });

  const handleRunClick = (runId: string) => {
    selectRun(runId === selectedRunId ? null : runId);
  };

  return (
    <div>
      <div className="px-5 pt-4 pb-2 flex items-center justify-between">
        <span className="text-xs text-text-muted font-medium">Runs</span>
        {runs && runs.length > 0 && (
          <span className="text-[10px] text-text-muted font-mono tabular-nums">{runs.length}</span>
        )}
      </div>

      {/* Start run form */}
      {selectedTopologyId && (
        <div className="px-5 py-3 space-y-3">
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Enter input for the topology..."
            className="w-full bg-surface/30 border border-border-subtle/15 rounded-lg px-3 py-2.5 text-sm text-text-primary placeholder-text-muted resize-none h-20 focus:outline-none focus:border-brand-purple/30 transition-colors duration-200 leading-relaxed"
            onKeyDown={(e) => {
              if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
                handleStartRun();
              }
            }}
          />
          <div className="flex items-center justify-between">
            <label className="flex items-center gap-2 text-xs text-text-secondary cursor-pointer group">
              <input
                type="checkbox"
                checked={supervised}
                onChange={(e) => setSupervised(e.target.checked)}
                className="rounded border-border-accent accent-brand-purple"
              />
              <span className="group-hover:text-text-primary transition-colors">
                Supervised
              </span>
            </label>
            <div className="flex items-center gap-2">
              <span className="text-[10px] text-text-muted tabular-nums">
                {input.length > 0 ? `${input.length} chars` : ""}
              </span>
              <button
                onClick={handleStartRun}
                disabled={!input.trim() || startRun.isPending}
                className="flex items-center gap-1.5 bg-brand-purple hover:bg-brand-purple/80 disabled:opacity-40 disabled:cursor-not-allowed text-white px-4 py-1.5 rounded-lg text-xs font-medium transition-[color,background-color,box-shadow,transform] duration-200 hover:shadow-glow-purple active:scale-[0.96]"
              >
                <Play className="w-3 h-3" />
                {startRun.isPending ? "Starting..." : "Run"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Filter row */}
      {runs && runs.length > 0 && (
        <div className="px-5 py-1.5 flex items-center gap-1 border-t border-border-subtle/10">
          <Filter className="w-3 h-3 text-text-muted" />
          {(["all", "running", "completed", "failed"] as StatusFilter[]).map((f) => (
            <button
              key={f}
              onClick={() => setStatusFilter(f)}
              className={`px-2 py-0.5 rounded text-[10px] font-medium transition-[color,background-color,transform] duration-200 active:scale-[0.96] ${
                statusFilter === f
                  ? "bg-brand-purple/10 text-brand-purple"
                  : "text-text-muted hover:text-text-secondary"
              }`}
            >
              {f === "all" ? "All" : f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
        </div>
      )}

      {/* Run list */}
      <div className="max-h-80 overflow-y-auto">
        {filteredRuns.length === 0 ? (
          <div className="p-6 text-sm text-text-muted text-center">
            {runs && runs.length > 0 ? "No matching runs" : "No runs yet"}
          </div>
        ) : (
          filteredRuns.map((run) => (
            <button
              key={run.id}
              onClick={() => handleRunClick(run.id)}
              className={`w-full text-left px-5 py-3 text-sm transition-[color,background-color,border-color,transform] duration-200 border-l-2 active:scale-[0.98] ${
                selectedRunId === run.id
                  ? "bg-brand-purple/5 border-l-brand-purple"
                  : "hover:bg-surface-lighter/5 border-l-transparent"
              }`}
            >
              {/* Input preview */}
              <div className="flex items-start justify-between gap-2 mb-1.5">
                <p className="text-xs text-text-primary font-medium leading-relaxed line-clamp-2">
                  {run.input.slice(0, 80)}
                  {run.input.length > 80 ? "..." : ""}
                </p>
                <StatusBadge status={run.status} />
              </div>

              {/* Meta row */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3 text-[11px] text-text-muted">
                  <span className="font-mono tabular-nums">${run.totalCostUsd.toFixed(4)}</span>
                  <span className="font-mono tabular-nums">
                    {formatTokens(run.totalTokens.input + run.totalTokens.output)} tok
                  </span>
                  {run.startedAt && (
                    <span className="flex items-center gap-1 tabular-nums">
                      <Clock className="w-3 h-3" />
                      {formatDistanceToNow(run.startedAt, { addSuffix: false })}
                    </span>
                  )}
                </div>

                {(run.status === "running" || run.status === "paused") && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      cancelRun.mutate(run.id);
                    }}
                    className="flex items-center gap-1 text-brand-pink hover:text-brand-pink/80 text-[11px] transition-[color,transform] duration-200 active:scale-[0.96]"
                  >
                    <X className="w-3 h-3" />
                    Cancel
                  </button>
                )}
              </div>

              {/* Supervised badge */}
              {run.supervised && (
                <span className="inline-block mt-1.5 text-[9px] px-1.5 py-0.5 rounded bg-amber-400/10 text-amber-400 font-medium">
                  Supervised
                </span>
              )}
            </button>
          ))
        )}
      </div>

    </div>
  );
}

function formatTokens(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}k`;
  return String(n);
}
